import { useMutation } from '@tanstack/react-query'
import { comparer } from 'mobx'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { queryClient } from '../../../../../App'
import { useGetReceiverModels } from '../../../../../hooks/useGetReceiverModels'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import {
  isAdmin,
  isAdministration,
  isDealer,
  isOperator,
} from '../../../../../services/AccountService'
import { changeDataClarifyServiceRequest } from '../../../../../services/NewApiService'
import { notifyError } from '../../../../../services/ToastifyService/ToastifyService'

export const useServiceInformationAccordion = (
  serviceStore: any,
  type: number,
) => {
  const invalidate = useInvalidateQueries()
  const [isEdit, setIsEdit] = useState(false)
  const intl = useIntl()
  const {
    serviceInformationAccordionFormStore,
    getServicePhotosFunc,
    currentServiceData,
    updateOrders,
    photosId,
    clearServicePhotosUrls,
    servicePhotos,
  } = serviceStore

  const isToGuarantee = currentServiceData?.status === 8
  const { data: receiveresModels } = useGetReceiverModels()

  const { mutateAsync, isPending } = useMutation(
    {
      mutationKey: ['changeDataClarifyServiceRequest'],
      mutationFn: () =>
        changeDataClarifyServiceRequest(
          currentServiceData.id,
          getParams(serviceInformationAccordionFormStore, isToGuarantee),
        ),
      onSuccess(res) {
        if (
          res?.images.length &&
          res?.images?.some((image: any) => !image.isValid)
        ) {
          res?.images.forEach((image: any) => {
            if (image.error === 'Дубликат') {
              notifyError(`Дубликат: ${image.filename}`)
            }
          })
        } else {
          invalidate([
            'single-service-report-table',
            'create-service-report-table',
            'get-suits-ids-for-create',
            'service-table',
            'get-service-history',
            'add-requests-to-service-report-table',
          ])

          if (isDealer()) {
            clearServicePhotosUrls()
            getServicePhotosFunc(currentServiceData.id, [
              'Clarification',
              'WarrantyServiceRequired',
              'MaintenanceImpossible',
              'ImprovementRequired',
              'MaintenanceCompleted',
              'DiagnosticCompleted',
            ])
          }
          updateOrders(res)
          setIsEdit(false)
          serviceInformationAccordionFormStore.clear()
        }
      },
    },
    queryClient,
  )

  useEffect(() => {
    if (isOperator() || isAdministration()) {
      return
    } else if (photosId !== currentServiceData.id) {
      getServicePhotosFunc(currentServiceData.id, [
        'Clarification',
        'WarrantyServiceRequired',
        'MaintenanceImpossible',
        'ImprovementRequired',
        'MaintenanceCompleted',
        'DiagnosticCompleted',
      ])
    }

    if (type === 1) {
      serviceInformationAccordionFormStore.data.diagnosticPassBasisComment = ' '
    }
  }, [])

  const shouldGetModelInfo: boolean = type === 2 && (isDealer() || isAdmin())

  const canEditSubscriberData =
    currentServiceData?.status === 8 ||
    (isDealer() &&
      ((14 === currentServiceData?.status &&
        currentServiceData?.dataCheckingStatus === 2) ||
        ([6.2].includes(currentServiceData.status) &&
          currentServiceData?.dataCheckingStatus === 0) ||
        ([6.2, 6.5].includes(currentServiceData.status) &&
          currentServiceData?.dataCheckingStatus === 2))) ||
    (isAdmin() &&
      comparer.default(currentServiceData?.status, 13) &&
      ((comparer.default(currentServiceData?.reportStatus, 3) &&
        !comparer.default(currentServiceData?.dataCheckingStatus, 3)) ||
        comparer.default(currentServiceData?.reportStatus, 2)))

  const handleEdit = () => setIsEdit((prev) => !prev)

  useEffect(() => {
    if (currentServiceData?.status === 20 && isEdit) setIsEdit(false)
  }, [currentServiceData?.status])

  const onReset = () => {
    setIsEdit(false)
    serviceInformationAccordionFormStore.clear()
  }

  const pictures = Object.values(servicePhotos).filter(
    (val: any) => val.isExist,
  )

  return {
    canEditSubscriberData,
    isEdit,
    handleEdit,
    intl,
    shouldGetModelInfo,
    receiveresModels,
    mutateAsync,
    currentServiceData,
    serviceInformationAccordionFormStore,
    onReset,
    isPending,
    pictures,
  }
}

const getParams = (
  serviceInformationAccordionFormStore: any,
  isToGuarantee: boolean,
) => {
  const params: Record<string, any> = {}

  const files =
    serviceInformationAccordionFormStore.data['files'] &&
    serviceInformationAccordionFormStore.data['files'].map((el: any) => el.file)

  if (files) {
    params.files = files
  }
  if (!isToGuarantee) {
    params.dateOfMaintenance = moment(
      serviceInformationAccordionFormStore.data?.dateOfMaintenance,
      'DD.MM.YYYYTHH:mm',
    ).format('YYYY-MM-DDTHH:mm:ssZ')
    params.workDescription =
      serviceInformationAccordionFormStore.data.workDescription
  }

  if (isToGuarantee) {
    params.dateOfVisit = moment(
      serviceInformationAccordionFormStore.data?.dateOfVisit,
      'DD.MM.YYYYTHH:mm',
    ).format('YYYY-MM-DDTHH:mm:ssZ')
    params.visitDescription =
      serviceInformationAccordionFormStore.data.visitDescription
  }

  return params
}

export const forbiddenPhotoStatuses = [6.3, 6.4, 6.5, 6.2, 13]
